/**
 * Admin Cash Tickets Page - manual ticket issuance without payment.
 * Native mobile app style with server-side user search.
 * Supports multi-currency pricing with currency selector.
 *
 * When embedded in AdminOrdersPage, uses shared event context.
 * When standalone, shows own event selector.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import { Loader } from "../../../components/Loader.tsx";
import {
  IconAlertTriangle,
  IconCheck,
  IconSearch,
  IconTicket,
  IconUser,
} from "../../../components/Icons.tsx";
import {
  getTelegram,
  haptic,
  safeShowAlert,
  safeShowConfirm,
} from "../../../lib/telegram.ts";
import { getErrorMessage } from "../../../lib/graphql.ts";
import { useAuth } from "../../../lib/auth.tsx";
import { useDebouncedValue } from "../../../lib/use-debounce.ts";
import { getCurrencySymbol } from "../../../lib/format.ts";
import { useAdminOrdersContext } from "../orders/context/index.ts";
import { useSearchAccounts, type SearchAccountResult } from "../shared/hooks/useSearchAccounts";
import { useEventTicketTypes, type EventTicketType } from "./hooks/useEventTicketTypes";
import { useIssueTicketsForCash } from "./hooks/useTicketMutations";

// =============================================================================
// Types
// =============================================================================

// Types imported from hooks:
// - SearchAccountResult from useSearchAccounts
// - EventTicketType from useEventTicketTypes

// Alias for compatibility with existing code
type TicketTypeWithPrices = EventTicketType;

// =============================================================================
// Helpers
// =============================================================================

function formatAccountLabel(account: SearchAccountResult): string {
  const parts = [account.firstName, account.lastName].filter(Boolean);
  if (parts.length > 0) {
    const name = parts.join(" ");
    return account.username ? `${name} (@${account.username})` : name;
  }
  return account.username ? `@${account.username}` : (account.telegramId ?? account.id);
}

/**
 * Get available currencies from ticket types.
 * Returns currencies that have prices defined for ALL ticket types.
 */
function getAvailableCurrencies(ticketTypes: TicketTypeWithPrices[]): string[] {
  if (ticketTypes.length === 0) return [];

  // Get all currencies from first ticket type
  const firstPrices = ticketTypes[0]?.prices ?? [];
  const currencies = firstPrices.map((p) => p.currency);

  // Filter to currencies that exist in ALL ticket types
  return currencies.filter((currency) =>
    ticketTypes.every((tt) => tt.prices.some((p) => p.currency === currency)),
  );
}

/**
 * Get price for a ticket type in a specific currency.
 */
function getPriceInCurrency(
  ticketType: TicketTypeWithPrices,
  currency: string,
): number {
  const priceObj = ticketType.prices.find((p) => p.currency === currency);
  return priceObj ? parseFloat(priceObj.price) : 0;
}

/**
 * Calculate total price (base + fees) for a ticket type in a specific currency.
 * 
 * For PERCENT fees: calculates based on the base price in the target currency.
 * For FIXED fees: looks up the price in the target currency from feeConfiguration.prices,
 *                 or applies proportional conversion if no price exists for that currency.
 */
function getTotalPriceInCurrency(
  ticketType: TicketTypeWithPrices,
  targetCurrency: string,
): number {
  const basePrice = getPriceInCurrency(ticketType, targetCurrency);
  if (basePrice === 0) return 0;

  // Get default currency price for proportional fee calculation
  const defaultCurrency = ticketType.currency ?? "THB";
  const defaultPrice = parseFloat(ticketType.price ?? "0");

  let feesAmount = 0;

  // Use fees array (with full feeConfiguration data) if available
  const feesData = ticketType.fees ?? [];
  
  for (const fee of feesData) {
    if (!fee.isActive) continue;
    
    const config = fee.feeConfiguration;
    const feeAmount = fee.overrideAmount 
      ? parseFloat(fee.overrideAmount) 
      : parseFloat(config.amount);

    if (config.calculation === "PERCENT") {
      // Percentage fee: calculate from base price in target currency
      feesAmount += basePrice * (feeAmount / 100);
    } else {
      // FIXED fee: look for price in target currency
      const priceInTargetCurrency = config.prices.find(
        (p) => p.currency === targetCurrency
      );

      if (priceInTargetCurrency) {
        // Use the specific price for this currency
        feesAmount += parseFloat(priceInTargetCurrency.amount);
      } else if (config.currency === targetCurrency) {
        // Legacy single-currency fee matches target
        feesAmount += feeAmount;
      } else {
        // Proportional conversion: fee_target = fee_source * (price_target / price_source)
        // This matches the server-side addSyntheticFeePricesForCurrency logic
        const sourceFeeAmount = config.prices.find(
          (p) => p.currency === defaultCurrency
        );
        const sourceAmount = sourceFeeAmount 
          ? parseFloat(sourceFeeAmount.amount) 
          : (config.currency === defaultCurrency ? feeAmount : 0);
        
        if (sourceAmount > 0 && defaultPrice > 0) {
          feesAmount += sourceAmount * (basePrice / defaultPrice);
        }
      }
    }
  }

  // Fallback to feesBreakdown if fees array is empty (legacy support)
  if (feesData.length === 0 && ticketType.feesBreakdown.length > 0) {
    for (const fee of ticketType.feesBreakdown) {
      if (fee.type === "PERCENT" || fee.type === "PERCENTAGE") {
        feesAmount += basePrice * (parseFloat(fee.amount) / 100);
      } else {
        // For FIXED fees in feesBreakdown, apply proportional conversion
        // feesBreakdown contains amount in default currency
        const feeAmountInDefault = parseFloat(fee.amount);
        if (defaultPrice > 0) {
          feesAmount += feeAmountInDefault * (basePrice / defaultPrice);
        }
      }
    }
  }

  return basePrice + feesAmount;
}

// =============================================================================
// Component
// =============================================================================

export interface AdminCashTicketsPageProps {
  /** When true, render without Layout and BackButton (e.g. inside orders page panel) */
  embedded?: boolean;
  /** Called when user closes the embedded view */
  onClose?: () => void;
}

export function AdminCashTicketsPage({
  embedded = false,
}: AdminCashTicketsPageProps = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tg = getTelegram();
  const { account } = useAuth();

  // Try to get event from context (when embedded)
  let contextEventId: string | null = null;
  try {
    const context = useAdminOrdersContext();
    contextEventId = context.selectedEventId;
  } catch {
    // Not in context - standalone mode
  }

  // Get eventId: from context when embedded, from URL when standalone
  const urlEventId = searchParams.get("eventId");
  const eventId = embedded ? contextEventId : urlEventId;

  const [selectedAccount, setSelectedAccount] = useState<SearchAccountResult | null>(
    null,
  );
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);

  // Debounced search query (300ms)
  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  // URQL hooks for data fetching
  const {
    accounts: searchResults,
    isLoading: isSearching,
  } = useSearchAccounts({
    query: debouncedQuery,
    limit: 15,
  });

  const {
    event: eventData,
    ticketTypes,
    isLoading: isLoadingEvent,
    error: eventError,
    refetch: refetchEvent,
  } = useEventTicketTypes({
    eventId: eventId ?? "",
    pause: !eventId,
  });

  const {
    issueTicketsForCash,
    isLoading: isSubmitting,
  } = useIssueTicketsForCash();

  const error = eventError?.message ?? null;

  // Available currencies from ticket types
  const availableCurrencies = useMemo(
    () => getAvailableCurrencies(ticketTypes),
    [ticketTypes],
  );

  // Current display currency (selected or default)
  const displayCurrency = useMemo(() => {
    if (selectedCurrency && availableCurrencies.includes(selectedCurrency)) {
      return selectedCurrency;
    }
    // Default to first available currency or THB
    return availableCurrencies[0] ?? "THB";
  }, [selectedCurrency, availableCurrencies]);

  // Auto-select first currency when event loads
  useEffect(() => {
    if (availableCurrencies.length > 0 && !selectedCurrency) {
      setSelectedCurrency(availableCurrencies[0]);
    }
  }, [availableCurrencies, selectedCurrency]);

  // BackButton - only when not embedded
  useEffect(() => {
    if (!tg || embedded) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin/orders");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, embedded]);

  // Reset quantities and currency when event changes
  useEffect(() => {
    if (eventId) {
      setQuantities({});
      setSelectedCurrency(null);
    }
  }, [eventId]);

  // Initialize quantities when event data loads
  useEffect(() => {
    if (ticketTypes.length > 0) {
      setQuantities((prev) => {
        const next = { ...prev };
        ticketTypes.forEach((tt) => {
          if (next[tt.id] === undefined) next[tt.id] = 0;
        });
        return next;
      });
    }
  }, [ticketTypes]);

  const items =
    ticketTypes
      .filter((tt) => (quantities[tt.id] ?? 0) > 0)
      .map((tt) => ({
        ticketTypeId: tt.id,
        quantity: quantities[tt.id] ?? 0,
      }));

  // Calculate totals in selected currency
  const { subtotal, feesTotal, total } = useMemo(() => {
    return ticketTypes.reduce(
      (acc, tt) => {
        const quantity = quantities[tt.id] ?? 0;
        if (quantity === 0) return acc;

        const basePrice = getPriceInCurrency(tt, displayCurrency);
        const totalPrice = getTotalPriceInCurrency(tt, displayCurrency);
        const fees = totalPrice - basePrice;

        return {
          subtotal: acc.subtotal + basePrice * quantity,
          feesTotal: acc.feesTotal + fees * quantity,
          total: acc.total + totalPrice * quantity,
        };
      },
      { subtotal: 0, feesTotal: 0, total: 0 },
    );
  }, [ticketTypes, quantities, displayCurrency]);

  const currencySymbol = getCurrencySymbol(displayCurrency);

  const canSubmit = !!selectedAccount && !!eventId && items.length > 0;

  const handleCurrencySelect = useCallback((currency: string) => {
    haptic("light");
    setSelectedCurrency(currency);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !selectedAccount || !eventId || items.length === 0)
      return;

    const message =
      "Выдать билеты выбранному пользователю? Они будут отправлены в Telegram.";
    const perform = async () => {
      haptic("light");
      try {
        const result = await issueTicketsForCash(
          selectedAccount.id,
          eventId,
          items,
        );
        if (result) {
          haptic("success");
          const msg = result.message ?? "Билеты выданы.";
          safeShowAlert(msg);
          setSelectedAccount(null);
          setSearchQuery("");
          setQuantities({});
        } else {
          haptic("error");
        }
      } catch (err) {
        haptic("error");
        safeShowAlert(getErrorMessage(err, "Ошибка выдачи билетов"));
      }
    };

    safeShowConfirm(message, (ok: boolean) => {
      if (ok) perform();
    });
  }, [canSubmit, selectedAccount, eventId, items, issueTicketsForCash]);

  const updateQuantity = (ticketTypeId: string, delta: number) => {
    haptic("light");
    setQuantities((prev) => {
      const ticketType = ticketTypes.find(
        (t) => t.id === ticketTypeId,
      );
      if (!ticketType) return prev;
      const maxAvailable = ticketType.available ?? 999;
      const current = prev[ticketTypeId] ?? 0;
      const next = Math.max(0, Math.min(maxAvailable, current + delta));
      return { ...prev, [ticketTypeId]: next };
    });
  };

  const handleSelectAccount = (acc: SearchAccountResult) => {
    haptic("light");
    setSelectedAccount(acc);
    setSearchQuery("");
    // searchResults is managed by useSearchAccounts hook, clearing query is enough
  };

  const handleClearAccount = () => {
    haptic("light");
    setSelectedAccount(null);
  };

  const Wrapper = embedded ? "div" : Layout;

  // Error state
  if (error) {
    return (
      <Wrapper>
        <div className="card p-4 xs:p-5 sm:p-6 text-center">
          <IconAlertTriangle className="w-10 h-10 xs:w-12 xs:h-12 mx-auto mb-3 xs:mb-4 text-amber-500" />
          <h2 className="text-lg xs:text-xl font-bold text-slate-900 mb-2">
            Ошибка загрузки
          </h2>
          <p className="text-slate-600 mb-4 xs:mb-6 text-sm">{error}</p>
          <button
            onClick={() => {
              if (eventId) refetchEvent();
            }}
            className="btn-primary w-full"
          >
            Попробовать снова
          </button>
        </div>
      </Wrapper>
    );
  }

  // No event selected (embedded mode)
  if (embedded && !eventId) {
    return (
      <div className="card p-6 text-center">
        <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-slate-100 flex items-center justify-center">
          <IconAlertTriangle className="w-6 h-6 text-slate-400" />
        </div>
        <h3 className="text-[15px] font-semibold text-slate-900 mb-2">
          Событие не выбрано
        </h3>
        <p className="text-[13px] text-slate-500">
          Выберите событие в заголовке выше, чтобы выдать билеты
        </p>
      </div>
    );
  }

  // Loading event
  if (!account || (eventId && isLoadingEvent && !eventData)) {
    return (
      <Wrapper>
        <PageLoader message="Загрузка..." />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {/* Header with icon */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
          <IconTicket className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Выдать билеты</h1>
          <p className="text-[13px] text-slate-500">
            {embedded && eventData
              ? eventData.title
              : "Ручная выдача без оплаты"}
          </p>
        </div>
      </div>

      {/* User selection - inline with search */}
      <div className="card p-4 mb-4">
        <p className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-3">
          Пользователь
        </p>

        {/* Selected user display */}
        {selectedAccount ? (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[var(--color-accent)] text-white">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <IconUser className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate text-[15px]">
                {formatAccountLabel(selectedAccount)}
              </p>
              {selectedAccount.username && (
                <p className="text-[13px] opacity-80">
                  @{selectedAccount.username}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleClearAccount}
              className="flex-shrink-0 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm active:opacity-80"
              aria-label="Убрать"
            >
              ×
            </button>
          </div>
        ) : (
          <>
            {/* Search input */}
            <div className="relative mb-3">
              <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Введите имя или @username..."
                className="input-field pl-9"
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-xs leading-none active:opacity-80"
                  aria-label="Очистить"
                >
                  ×
                </button>
              )}
            </div>

            {/* Search results / states */}
            <div className="max-h-[240px] overflow-y-auto -mx-1 px-1">
              {/* Empty state - no query */}
              {!searchQuery && (
                <p className="text-[14px] text-slate-400 text-center py-6">
                  Введите имя или @username для поиска
                </p>
              )}

              {/* Query too short */}
              {searchQuery && searchQuery.length < 2 && (
                <p className="text-[14px] text-slate-400 text-center py-6">
                  Введите минимум 2 символа
                </p>
              )}

              {/* Searching */}
              {searchQuery.length >= 2 && isSearching && (
                <div className="flex items-center justify-center gap-2 py-6 text-slate-500">
                  <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                  <span className="text-[14px]">Поиск...</span>
                </div>
              )}

              {/* No results */}
              {searchQuery.length >= 2 &&
                !isSearching &&
                searchResults.length === 0 && (
                  <p className="text-[14px] text-slate-400 text-center py-6">
                    Пользователь не найден
                  </p>
                )}

              {/* Results list */}
              {searchQuery.length >= 2 &&
                !isSearching &&
                searchResults.length > 0 && (
                  <div className="space-y-1">
                    {searchResults.map((acc) => (
                      <button
                        key={acc.id}
                        type="button"
                        onClick={() => handleSelectAccount(acc)}
                        className="w-full flex items-center gap-3 min-h-[52px] px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 active:bg-slate-100 transition-colors text-left"
                      >
                        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                          <IconUser className="w-4 h-4 text-slate-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate text-[14px]">
                            {formatAccountLabel(acc)}
                          </p>
                          {acc.username && (
                            <p className="text-[12px] text-slate-500">
                              @{acc.username}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
            </div>
          </>
        )}
      </div>

      {/* Ticket types - only when event is loaded */}
      {isLoadingEvent ? (
        <div className="card p-6">
          <div className="flex items-center justify-center gap-2 text-slate-500">
            <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
            <span className="text-sm">Загрузка билетов...</span>
          </div>
        </div>
      ) : eventData ? (
        <div className="card p-4 mb-4">
          <p className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-3">
            Типы билетов
          </p>
          {ticketTypes.filter((tt) => tt.isActive).length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">
              Нет доступных типов билетов
            </p>
          ) : (
            <div className="space-y-3">
              {ticketTypes
                .filter((tt) => tt.isActive)
                .map((tt) => {
                  const quantity = quantities[tt.id] ?? 0;
                  const available = tt.available;
                  const over = available != null && quantity > available;
                  const priceInCurrency = getPriceInCurrency(
                    tt,
                    displayCurrency,
                  );
                  return (
                    <div
                      key={tt.id}
                      className={`rounded-xl p-4 border transition-all ${
                        quantity > 0
                          ? "border-emerald-300 bg-emerald-50/50"
                          : "border-slate-200 bg-slate-50/50"
                      } ${over ? "border-red-300 bg-red-50/50" : ""}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1 pr-3">
                          <h3 className="font-medium text-slate-900 text-[15px]">
                            {tt.name}
                          </h3>
                          <p className="text-[12px] text-slate-500 mt-0.5">
                            {available == null
                              ? "Без лимита"
                              : `Доступно: ${available}`}
                            {priceInCurrency > 0 &&
                              ` · ${priceInCurrency.toFixed(0)} ${currencySymbol}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => updateQuantity(tt.id, -1)}
                            disabled={quantity === 0}
                            className="w-11 h-11 rounded-xl border border-slate-200 flex items-center justify-center text-slate-600 active:bg-slate-100 disabled:opacity-40 transition-colors text-lg font-medium"
                          >
                            −
                          </button>
                          <span className="w-8 text-center text-[17px] font-semibold text-slate-900 tabular-nums">
                            {quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(tt.id, 1)}
                            disabled={
                              available != null && quantity >= available
                            }
                            className="w-11 h-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center active:bg-emerald-600 disabled:opacity-40 transition-colors text-lg font-medium"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      ) : null}

      {/* Currency selector - only when multiple currencies available */}
      {availableCurrencies.length > 1 && (
        <div className="card p-4 mb-4">
          <p className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-3">
            Валюта
          </p>
          <div className="flex gap-2">
            {availableCurrencies.map((currency) => (
              <button
                key={currency}
                type="button"
                onClick={() => handleCurrencySelect(currency)}
                className={`flex-1 py-2.5 rounded-xl text-[14px] font-medium transition-all ${
                  displayCurrency === currency
                    ? "bg-[var(--color-accent)] text-white shadow-md"
                    : "bg-slate-100 border border-slate-200 text-slate-700 active:bg-slate-200"
                }`}
              >
                {getCurrencySymbol(currency)} {currency}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Summary / Calculator */}
      {total > 0 && (
        <div className="card p-4 mb-4">
          <p className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-3">
            Итого
          </p>
          <div className="space-y-2">
            <div className="flex justify-between text-[14px] text-slate-600">
              <span>Билеты</span>
              <span>
                {subtotal.toFixed(0)} {currencySymbol}
              </span>
            </div>
            {feesTotal > 0 && (
              <div className="flex justify-between text-[14px] text-slate-500">
                <span>Сервисный сбор</span>
                <span>
                  {feesTotal.toFixed(0)} {currencySymbol}
                </span>
              </div>
            )}
          </div>
          <div className="border-t border-slate-200 mt-3 pt-3 flex justify-between items-center">
            <span className="font-medium text-slate-900 text-[15px]">
              К оплате
            </span>
            <span className="font-bold text-[20px] text-[var(--color-accent)]">
              {total.toFixed(0)} {currencySymbol}
            </span>
          </div>
        </div>
      )}

      {/* Submit button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit || isSubmitting}
        className="w-full min-h-[50px] rounded-xl bg-emerald-500 text-white text-[16px] font-medium active:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <IconCheck className="w-5 h-5" />
        Выдать билеты
      </button>

      {/* Hint */}
      {!canSubmit && eventData && (
        <p className="text-[13px] text-slate-500 mt-3 text-center">
          Выберите пользователя и укажите количество билетов
        </p>
      )}

      {/* Bottom spacing */}
      <div className="h-4" />

      {/* Processing overlay - fullscreen with pulsing heart */}
      {isSubmitting && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center">
          <div className="text-center">
            <Loader className="mx-auto mb-4" />
            <p className="text-white text-[16px] font-medium">
              Выдача билетов...
            </p>
            <p className="text-white/60 text-[13px] mt-1">
              Генерация и отправка в Telegram
            </p>
          </div>
        </div>
      )}
    </Wrapper>
  );
}
